import {
    DigitNumbers,
    DoubleDigitNumbers,
    Operations,
    SingleDigitNumbers,
    TimesTableNumbers,
    TripleDigitNumbers,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Which numbers an operand's range offers, by digit count and the sheet's
 * operations (ADR 0064).
 *
 * One digit means 0-9, except on a sheet that is only multiplication and
 * division, where it means the times tables to 12. Long division is left out:
 * its divisor is laid out as a single digit.
 *
 * This is the one place that decides it. The value-range control, the URL
 * decoder, the route builder, differentiation and the flash-card deck all ask
 * here, so none of them can disagree about what a sheet offers.
 */
export function bucketFor(digits: number, operations: Operations[]): DigitNumbers {
    if (digits === 3) {
        return TripleDigitNumbers;
    }
    if (digits === 2) {
        return DoubleDigitNumbers;
    }
    return isTimesTableSheet(operations) ? TimesTableNumbers : SingleDigitNumbers;
}

const TIMES_TABLE_OPERATIONS: Operations[] = [Operations.Multiplication, Operations.Division];

/** Every operation on the sheet is multiplication or division. */
export function isTimesTableSheet(operations: Operations[]): boolean {
    return operations.length > 0 && operations.every((op) => TIMES_TABLE_OPERATIONS.includes(op));
}

/** The operation values of a settings object, which stores the whole option. */
export function operationsOf(settings: Pick<ISettingsProps, "Operators">): Operations[] {
    return (settings.Operators ?? []).map((op) => op.value);
}

/**
 * Brings each one-digit range into line with the sheet's operations, in place
 * (ADR 0064 §3). Called after the operations change, because they and the
 * ranges are separate controls and one can move out from under the other.
 *
 * The offered range becomes the bucket for the current operations. A selected
 * value that bucket no longer holds is dropped: adding + to a 0-12
 * multiplication sheet drops 10-12. If that would empty a selection that was
 * not empty, the whole bucket is selected instead, because an empty selection
 * is a blank sheet nobody asked for.
 *
 * With no operation selected the ranges are left alone. That is a moment
 * between two clicks, and trimming there would lose the 11s on the way from
 * one multiplication sheet to the next.
 */
export function fitOneDigitRanges<T extends Pick<ISettingsProps, "Operators" | "NumberRanges">>(
    settings: T
): T {
    const operations = operationsOf(settings);
    if (operations.length === 0) {
        return settings;
    }
    const bucket = bucketFor(1, operations);
    for (const range of settings.NumberRanges) {
        if (range.NumberOfDigits !== 1) {
            continue;
        }
        const kept = range.NumberRangeSelection.filter((value) => bucket.Range.includes(value));
        const emptied = kept.length === 0 && range.NumberRangeSelection.length > 0;
        range.NumberRange = [...bucket.Range];
        range.NumberRangeSelection = emptied ? [...bucket.Range] : kept;
    }
    return settings;
}

/**
 * Whether a range's values stand for buckets ("10s" = 10-19) rather than
 * themselves. The two- and three-digit ranges start at 10 and 100; a one-digit
 * range starts at 0, and its 10, 11 and 12 are the times tables (ADR 0064 §5).
 */
export function isBucketOfTens(numberRange: number[]): boolean {
    return numberRange.length > 0 && numberRange[0] >= 10;
}
